/* App shell (simple design, docs/mockup/simple): a grouped sidebar that lists every registered feature by its
   group (Work / Apps / You / Account), collapsible groups remembered in localStorage, a search box that filters
   features and their commands, and the main outlet. Below 768 px the sidebar is a drawer behind one Menu
   button in a bottom bar of Ask, Files, Create, Search (within Ask) and Menu. No title bar: account, view settings,
   notifications and approvals live at the foot of the sidebar. Ctrl+K opens the command palette, `?` the
   shortcut sheet. Accessibility: skip link, landmarks, focus moves to the main region when the screen changes
   (announced), Esc closes the drawer and returns focus to Menu. */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  Bell, ChevronDown, Download, FolderOpen, Info, Keyboard, LogOut, Menu as MenuIcon, MessageSquare, Monitor, Moon, OctagonX, PanelLeftOpen,
  Search, Sparkles, SquarePen, Sun, Tag, X,
} from 'lucide-react';
import { CommandPalette, Dialog, Menu, useToast, type PaletteItem } from '../ui';
import { cx } from '../ui/cx';
import { FEATURE_GROUPS, type FeatureDescriptor, type FeatureGroup } from './feature';
import { navForWidth, sizeClass, useElementWidth, type NavKind, type SizeClass } from './layout';
import { CREATE_IDS, loadClosedGroups, matchNav, saveClosedGroups } from './nav';
import { OfflineBanner } from './pages';
import { useInstallPrompt } from './pwa';
import { useSession } from './session';
import { useUi, type Theme } from './store';
import {
  AiDisclosureLink, ApprovalsBadge, CreditMeterSlot, KillSwitchDialog, NavBadge, NotificationsButton,
} from './widgets';

export interface LayoutInfo { width: number; size: SizeClass; nav: NavKind }
const LayoutCtx = createContext<LayoutInfo>({ width: 1280, size: 'large', nav: 'sidebar' });
/** Current shell width, size class and navigation kind (features use it for pane decisions). */
export const useLayout = () => useContext(LayoutCtx);

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

export const SHORTCUTS: [string, string][] = [
  [`${MOD}+K`, 'Search and commands'],
  [`${MOD}+B`, 'Hide or show the sidebar'],
  [`${MOD}+/  or  ?`, 'This list'],
  ['Enter', 'Send (Shift+Enter for a new line)'],
  ['Esc', 'Stop a reply, close a menu, popover, drawer or dialog'],
  ['Alt+Left / Alt+Right', 'Resize a split pane'],
  ['Tab', 'First stop: Skip to content'],
];


function FeatureIcon({ f, size = 20 }: { f: FeatureDescriptor; size?: number }) {
  const Icon = f.icon;
  return <Icon size={size} aria-hidden="true" />;
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || !!el.closest?.('[contenteditable="true"],[role="textbox"]');
};

/** Moves focus to the main region and announces the screen when the top-level screen changes (not on tab changes inside it). */
function RouteFocus() {
  const loc = useLocation();
  const section = loc.pathname.split('/')[1] ?? '';
  const prev = useRef<string | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (prev.current === null) { prev.current = section; return; }
    if (prev.current === section) return;
    prev.current = section;
    const main = document.getElementById('main');
    if (main && !main.contains(document.activeElement)) main.focus({ preventScroll: true });
    setMsg(`Opened ${document.title.replace(/ · Crosspoint AI \+$/, '')}`);
  }, [section]);
  return <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="route-announcer">{msg}</div>;
}

export interface ShellProps {
  features: FeatureDescriptor[];
  width?: number;
  children?: ReactNode;
  /** Live widgets that call the API (approvals, notifications, meter). Off in some tests. */
  widgets?: boolean;
}

export function Shell({ features, width: widthOverride, children, widgets = true }: ShellProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const measured = useElementWidth(rootRef);
  const width = widthOverride ?? measured;
  const size = sizeClass(width);
  const nav = navForWidth(width);
  const ui = useUi();
  const session = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const toast = useToast();
  const install = useInstallPrompt();
  const [keysOpen, setKeysOpen] = useState(false);
  const [killOpen, setKillOpen] = useState(false);
  const [closed, setClosed] = useState<FeatureGroup[]>(loadClosedGroups);
  const [query, setQuery] = useState('');
  const drawer = nav === 'bottom';
  const drawerOpen = drawer && ui.moreOpen;
  const hidden = !drawer && ui.navCollapsed;
  const navFeatures = useMemo(() => features.filter((f) => !f.bare && !f.public && !f.hidden), [features]);
  const hasAgents = features.some((f) => f.id === 'agents');
  const hasCreate = navFeatures.some((f) => CREATE_IDS.includes(f.id));
  const matches = useMemo(() => matchNav(navFeatures, query), [navFeatures, query]);
  const live = widgets;

  const onAsk = loc.pathname === '/ask' || loc.pathname.startsWith('/ask/');
  const inAskSearch = onAsk && new URLSearchParams(loc.search).get('tab') === 'history';

  const closeDrawer = (refocus = false) => { ui.setMore(false); if (refocus) setTimeout(() => menuBtnRef.current?.focus(), 0); };

  // Leave the drawer and clear the search when the screen changes.
  useEffect(() => { ui.setMore(false); setQuery(''); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opening the drawer puts focus on its search box.
  useEffect(() => { if (drawerOpen) searchRef.current?.focus(); }, [drawerOpen]);

  // Global shortcuts: Ctrl+K palette, Ctrl+B sidebar, Ctrl+/ or ? shortcut sheet, Esc closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape' && useUi.getState().moreOpen && !document.querySelector('[role="dialog"][aria-modal="true"],[role="menu"]')) { closeDrawer(true); return; }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) {
        if (e.key === '?' && !e.altKey && !isTyping(e.target) && !document.querySelector('[role="dialog"][aria-modal="true"]')) { e.preventDefault(); setKeysOpen(true); }
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'k') { e.preventDefault(); ui.setPalette(!useUi.getState().paletteOpen); }
      else if (k === 'b' && !e.shiftKey) { e.preventDefault(); ui.toggleNav(); }
      else if (k === '/') { e.preventDefault(); setKeysOpen((v) => !v); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ui]); // eslint-disable-line react-hooks/exhaustive-deps

  const paletteItems = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];
    for (const f of navFeatures) {
      items.push({ id: `go-${f.id}`, title: f.title, group: f.group, icon: <FeatureIcon f={f} size={16} />, hint: 'Open', keywords: [f.id, ...(f.short ? [f.short] : []), ...f.capabilities], run: () => navigate(f.route) });
    }
    if (hasCreate) items.push({ id: 'go-create', title: 'Create', group: 'Apps', hint: 'Open', keywords: ['make', 'new'], run: () => navigate('/create') });
    for (const f of features) {
      for (const c of f.commands ?? []) {
        items.push({
          id: `cmd-${f.id}-${c.id}`, title: c.title, group: 'Commands', hint: c.shortcut, keywords: [f.title, ...(c.keywords ?? [])],
          run: () => (c.run ? c.run({ navigate, toast: (m) => toast.show(m) }) : c.to && navigate(c.to)),
        });
      }
    }
    const theme = (t: Theme, label: string) => ({ id: `theme-${t}`, title: `Theme: ${label}`, group: 'Settings', keywords: ['dark', 'light', 'appearance'], run: () => ui.setTheme(t) });
    items.push(
      theme('system', 'match system'), theme('light', 'light'), theme('dark', 'dark'),
      { id: 'density', title: `Density: switch to ${ui.density === 'simple' ? 'Full' : 'Simple'}`, group: 'Settings', keywords: ['simple', 'full', 'compact'], run: () => ui.setDensity(ui.density === 'simple' ? 'full' : 'simple') },
      { id: 'nav', title: 'Hide or show the sidebar', group: 'Settings', hint: `${MOD}+B`, keywords: ['sidebar', 'navigation'], run: () => ui.toggleNav() },
      { id: 'keys', title: 'Keyboard shortcuts', group: 'Settings', hint: '?', run: () => setKeysOpen(true) },
      { id: 'ai-info', title: 'About AI-generated content', group: 'Help', keywords: ['label', 'disclosure', 'c2pa', 'report'], run: () => navigate('/settings/safety') },
      { id: 'signout', title: 'Sign out', group: 'Account', run: () => void session.signOut() },
    );
    if (hasAgents) {
      items.push({ id: 'kill', title: 'Stop all agents now', group: 'Agents', keywords: ['kill switch', 'emergency', 'stop'], run: () => setKillOpen(true) });
    }
    if (install.canInstall) items.push({ id: 'install', title: 'Install Crosspoint AI + as an app', group: 'Settings', keywords: ['pwa', 'desktop'], run: () => void install.install() });
    if (import.meta.env.DEV) {
      items.push({ id: 'ids', title: `${ui.ids ? 'Hide' : 'Show'} capability IDs`, group: 'Developer', keywords: ['caps', 'badges'], run: () => ui.setIds(!ui.ids) });
      items.push({ id: 'gallery', title: 'Component gallery', group: 'Developer', keywords: ['ui', 'storybook'], run: () => navigate('/_ui') });
    }
    return items;
  }, [features, navFeatures, hasCreate, navigate, toast, ui, session, hasAgents, install]);

  const user = session.user;
  const themeIcon = ui.theme === 'dark' ? Moon : ui.theme === 'light' ? Sun : Monitor;
  const nextTheme: Theme = ui.theme === 'system' ? 'light' : ui.theme === 'light' ? 'dark' : 'system';

  const toggleGroup = (g: FeatureGroup) => {
    const next = closed.includes(g) ? closed.filter((x) => x !== g) : [...closed, g];
    setClosed(next);
    saveClosedGroups(next);
  };

  const openMatch = (m: (typeof matches)[number]) => {
    setQuery('');
    if (m.kind === 'feature') navigate(m.feature.route);
    else if (m.command.run) m.command.run({ navigate, toast: (t) => toast.show(t) });
    else if (m.command.to) navigate(m.command.to);
    if (drawer) closeDrawer();
  };

  const accountMenu = (
    <Menu
      label="Account and view"
      align="start"
      asSheet="auto"
      trigger={(p) => (
        <button type="button" className="icon-btn" {...p} ref={p.ref} aria-label="Account and view" title="Account and view">
          <span className="avatar" style={{ width: 32, height: 32, fontSize: 14 }}>{user?.initials ?? '?'}</span>
        </button>
      )}
      groups={[
        { label: user?.name ?? 'Account', items: [{ id: 'email', label: user?.email ?? 'Signed in', disabled: true }] },
        {
          label: 'View',
          items: [
            { id: 'search', label: 'Search and commands', icon: Search, hint: `${MOD}+K`, onSelect: () => ui.setPalette(true) },
            { id: 'theme', label: `Theme: ${ui.theme === 'system' ? 'match system' : ui.theme}`, icon: themeIcon, keepOpen: true, onSelect: () => ui.setTheme(nextTheme) },
            { id: 'density', label: 'Full density', checked: ui.density === 'full', keepOpen: true, onSelect: () => ui.setDensity(ui.density === 'full' ? 'simple' : 'full') },
            ...(import.meta.env.DEV ? [{ id: 'ids', label: 'Show capability IDs', icon: Tag, checked: ui.ids, keepOpen: true, onSelect: () => ui.setIds(!ui.ids) }] : []),
            { id: 'keys', label: 'Keyboard shortcuts', icon: Keyboard, hint: '?', onSelect: () => setKeysOpen(true) },
            ...(install.canInstall ? [{ id: 'install', label: 'Install app', icon: Download, cap: 'DEV-06', onSelect: () => void install.install() }] : []),
          ],
        },
        ...(hasAgents ? [{ label: 'Agents', items: [{ id: 'kill', label: 'Stop all agents', icon: OctagonX, danger: true, cap: 'AGENT-11', onSelect: () => setKillOpen(true) }] }] : []),
        {
          label: 'Help', items: [
            { id: 'ai', label: 'About AI-generated content', icon: Info, cap: 'SAFE-01', onSelect: () => navigate('/settings/safety') },
            { id: 'notifications', label: 'Notification settings', icon: Bell, onSelect: () => navigate('/settings/personal#notifications') },
          ],
        },
        { label: 'Session', items: [{ id: 'signout', label: 'Sign out', icon: LogOut, onSelect: () => void session.signOut() }] },
      ]}
    />
  );

  const showSidebar = drawer ? drawerOpen : !hidden;
  const badged = navFeatures.filter((f) => f.badge || f.useBadge);

  const sidebar = showSidebar && (
    <nav
      className="side"
      id="side-nav"
      aria-label="Main"
      data-drawer={drawer || undefined}
      onKeyDown={drawer ? (e) => {
        // The open drawer keeps Tab inside it (the rest of the page is inert).
        if (e.key !== 'Tab') return;
        const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter((el) => !el.closest('[hidden]'));
        if (!items.length) return;
        const a = document.activeElement;
        if (!e.shiftKey && a === items[items.length - 1]) { e.preventDefault(); items[0]!.focus(); }
        else if (e.shiftKey && a === items[0]) { e.preventDefault(); items[items.length - 1]!.focus(); }
      } : undefined}
    >
      <div className="side-top">
        <span className="brand"><span className="logo" aria-hidden="true">+</span> Crosspoint AI +</span>
        {drawer
          ? <button type="button" className="icon-btn" aria-label="Close menu" onClick={() => closeDrawer(true)}><X size={20} aria-hidden="true" /></button>
          : <button type="button" className="icon-btn" aria-label="Hide sidebar" title={`Hide sidebar (${MOD}+B)`} onClick={ui.toggleNav}><PanelLeftOpen size={20} aria-hidden="true" /></button>}
      </div>
      <button type="button" className="newchat" onClick={() => navigate('/ask')}><SquarePen size={20} aria-hidden="true" /> New chat</button>
      <input
        ref={searchRef}
        className="search"
        type="search"
        aria-label="Search features and commands"
        placeholder="Search features"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && matches[0]) { e.preventDefault(); openMatch(matches[0]); }
          else if (e.key === 'Escape' && query) { e.preventDefault(); e.stopPropagation(); setQuery(''); }
        }}
      />
      <div className="side-scroll">
        {query.trim() ? (
          <ul className="results" aria-label="Search results">
            {matches.map((m) => (
              <li key={m.key}>
                <button type="button" className="nav" onClick={() => openMatch(m)}>
                  {m.kind === 'feature' && <FeatureIcon f={m.feature} />}
                  <span className="lbl">{m.title}</span>
                  <span className="hint">{m.group}</span>
                </button>
              </li>
            ))}
            {!matches.length && <li className="nomatch">Nothing matches. Press Ctrl+K to search chats and settings.</li>}
          </ul>
        ) : (
          FEATURE_GROUPS.map((g) => {
            const list = navFeatures.filter((f) => f.group === g);
            const hub = g === 'Apps' && hasCreate;
            if (!list.length && !hub) return null;
            const isOpen = !closed.includes(g);
            return (
              <div key={g} role="group" aria-label={g} className="grp">
                <button type="button" className="ghd" aria-expanded={isOpen} aria-controls={`grp-${g}`} onClick={() => toggleGroup(g)}>
                  <span>{g}</span><ChevronDown size={18} aria-hidden="true" />
                </button>
                <div className="items" id={`grp-${g}`} hidden={!isOpen}>
                  {hub && (
                    <NavLink to="/create" className="nav">
                      <Sparkles size={20} aria-hidden="true" /><span className="lbl">Create</span>
                    </NavLink>
                  )}
                  {list.map((f) => (
                    <NavLink key={f.id} to={f.route} className="nav">
                      <FeatureIcon f={f} /><span className="lbl">{f.title}</span><NavBadge f={f} />
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })
        )}
        {!navFeatures.length && <p className="nomatch">No screens yet.</p>}
      </div>
      <footer className="side-foot" aria-label="Account, credits and AI disclosure">
        {live && <CreditMeterSlot />}
        <div className="side-foot-row">
          {accountMenu}
          {live && <NotificationsButton />}
          {live && hasAgents && <ApprovalsBadge />}
        </div>
        <AiDisclosureLink />
      </footer>
    </nav>
  );

  return (
    <LayoutCtx.Provider value={{ width, size, nav }}>
      <div ref={rootRef} className="shell" data-size={size} data-nav={nav} data-hidden={hidden || undefined} data-testid="shell">
        <a href="#main" className="skip-link" onClick={(e) => { e.preventDefault(); document.getElementById('main')?.focus(); }}>Skip to content</a>
        <OfflineBanner />
        <div className="shell-body">
          {sidebar}
          {drawerOpen && <button type="button" className="dscrim fade" aria-label="Close menu" tabIndex={-1} onClick={() => closeDrawer(true)} />}
          <main id="main" className="shell-main" tabIndex={-1} inert={drawerOpen || undefined}>
            {hidden && (
              <button type="button" className="icon-btn side-reveal" aria-label="Show sidebar" title={`Show sidebar (${MOD}+B)`} onClick={ui.toggleNav}>
                <MenuIcon size={20} aria-hidden="true" />
              </button>
            )}
            {children ?? <Outlet />}
          </main>
        </div>
        {drawer && (
          <nav className="bbar" aria-label="Most used" inert={drawerOpen || undefined}>
            <Link to="/ask" className="bbar-i" aria-current={onAsk && !inAskSearch ? 'page' : undefined}>
              <MessageSquare size={22} aria-hidden="true" />Ask{badged.map((f) => <NavBadge key={f.id} f={f} />)}
            </Link>
            <NavLink to="/memory" className="bbar-i"><FolderOpen size={22} aria-hidden="true" />Files</NavLink>
            <NavLink to="/create" className="bbar-i"><Sparkles size={22} aria-hidden="true" />Create</NavLink>
            <Link to="/ask?tab=history" className="bbar-i" aria-current={inAskSearch ? 'page' : undefined}><Search size={22} aria-hidden="true" />Search</Link>
            <button ref={menuBtnRef} type="button" className={cx('bbar-i', drawerOpen && 'on')} aria-expanded={drawerOpen} aria-controls="side-nav" onClick={() => (drawerOpen ? closeDrawer() : ui.setMore(true))}>
              <MenuIcon size={22} aria-hidden="true" />Menu
            </button>
          </nav>
        )}
        <CommandPalette open={ui.paletteOpen} onClose={() => ui.setPalette(false)} items={paletteItems} />
        <Dialog open={keysOpen} onClose={() => setKeysOpen(false)} title="Keyboard shortcuts">
          <table>
            <tbody>
              {SHORTCUTS.map(([k, d]) => (
                <tr key={k}><td><kbd>{k}</kbd></td><td>{d}</td></tr>
              ))}
            </tbody>
          </table>
        </Dialog>
        <KillSwitchDialog open={killOpen} onClose={() => setKillOpen(false)} />
        <RouteFocus />
      </div>
    </LayoutCtx.Provider>
  );
}
